import { observer } from 'mobx-react';
import { FC } from 'react';
import { useForm, withForm } from 'react-bootstrap-formutil';
import ContactInformation from 'components/forms/ContactInformation';
import DeliveryAddress from 'components/forms/DeliveryAddress';
import HomeAddress from 'components/forms/HomeAddress';
import LegalResidenceCountry from 'components/forms/LegalResidenceCountry';
import SameResidenceAddr from 'components/forms/SameResidenceAddr';
import TaxCountry from 'components/forms/TaxCountry';
import TaxNumber from 'components/forms/TaxNumber';
import { Fade } from 'components/Transition';
import { useGetAllCountry } from 'hooks/useGetCountryList';
import security from 'stores/security';
import values from 'stores/values';
import { CustomerInfoType, IdTypeEnum } from 'types/customer';
import { checkFourCountryIsChn } from 'utils/helper';
import Container from '../components/Container';
import styles from './style.scss';

// 1. 非SSN-通讯及地址信息+税务信息
// 2. SSN-通讯及地址信息
const Address: FC = () => {
    const { email, phone, telCode } = security.userInfo;
    const {
        $params: {
            id_type,
            country,
            issue_country,
            tax_residence_country,
            legal_residence_country,
            same_delivery_and_residence,
            delivery_address_type,
            home_address_type
        }
    } = useForm<CustomerInfoType>();
    const { data: dataCountries } = useGetAllCountry();
    const isSSN = id_type === IdTypeEnum.SSN_IDS;
    const isFourChn = checkFourCountryIsChn({
        country,
        issueCountry: issue_country,
        legalResidenceCountry: legal_residence_country,
        taxResidenceCountry: tax_residence_country
    });

    return (
        <Container className={styles.root}>
            <h3 className="open-title mt-0">{__('CONTACT AND ADDRESS INFORMATION')}</h3>
            <ContactInformation
                disabled
                emailOptions={{
                    isPreview: !!email,
                    defaultValue: email
                }}
                phoneOptions={{
                    isPreview: !!phone,
                    defaultPhone: phone,
                    defaultTelCode: telCode
                }}
            />
            {isSSN ? (
                <>
                    <h3 className="open-title mt-0">
                        {__('RESIDENTIAL ADDRESS')}
                        <div className={styles.tips}>
                            <p className="mb-0">{__("We're legally required to collect this information.")}</p>
                            <p className="mb-0">
                                {__('This is usually the address on your government-issued ID and cannot be a PO box.')}
                            </p>
                            <p>* {__('A PO Box can be used as a mailing address, but not residential address')}</p>
                        </div>
                    </h3>
                    <HomeAddress homeAddressType={home_address_type} isSSN={isSSN} disabled />
                    {same_delivery_and_residence === '0' && (
                        <DeliveryAddress
                            deliveryAddressType={delivery_address_type}
                            legalResidenceCountry={legal_residence_country}
                            isSSN={isSSN}
                            isMarsco
                            disabled
                            countryDisabled
                        />
                    )}
                </>
            ) : (
                <>
                    <h3 className="open-title mt-0">{__('RESIDENTIAL ADDRESS')}</h3>
                    <p className="mb-3 normal-text-color">
                        {__("We're legally required to collect this information.")}
                    </p>
                    <LegalResidenceCountry
                        disabled
                        $defaultValue={legal_residence_country}
                        isSSN={isSSN}
                        countrySelectProps={{
                            extraCountryItems: dataCountries?.filter(
                                item => item.country_code === legal_residence_country
                            )
                        }}
                    />
                    {isFourChn ? (
                        <HomeAddress
                            disabled
                            isSSN={isSSN}
                            hideMailAddress
                            hasUSDeliveryAddress
                            homeAddressType={home_address_type}
                            legalResidenceCountry={legal_residence_country}
                        />
                    ) : (
                        <>
                            <HomeAddress disabled isSSN={isSSN} legalResidenceCountry={legal_residence_country} />
                            <SameResidenceAddr
                                disabled
                                idType={id_type}
                                issueCountry={issue_country}
                                legalResidenceCountry={legal_residence_country}
                            />
                            <Fade in={same_delivery_and_residence === '0'} exit={false}>
                                <DeliveryAddress
                                    disabled
                                    hasCountry
                                    deliveryAddressType={delivery_address_type}
                                    legalResidenceCountry={legal_residence_country}
                                    isSSN={isSSN}
                                    isFourChn={isFourChn}
                                />
                            </Fade>
                            <h3 className="open-title mt-4">{__('TAX INFORMATION')}</h3>
                            <TaxCountry
                                $defaultValue={tax_residence_country}
                                isSSN={isSSN}
                                disabled
                                countrySelectProps={{
                                    extraCountryItems: dataCountries?.filter(
                                        item => item.country_code === tax_residence_country
                                    )
                                }}
                            />
                            <TaxNumber disabled />
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

export default withForm(observer(Address), {
    $defaultValues: () => {
        values.setShowProgressBar(false);

        return security.customerInfo;
    }
});
